import { animateCurrentProgressBar, animateCurrentProgressBarInElement, prepareCurrentProgressBar, prepareCurrentProgressBarInElement } from "@/modules/actions/headerLineDuration";
import { trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import { buildMediaAssetViewParams } from "@/helpers/buildMediaAssetViewParams";
import { buildMediaAssetIdParam } from "@/helpers/buildMediaAssetIdParam";
import localStorageRepositories from "@/plugins/localStorageRepositories";
import { updateGroupSeenBorder } from "@/helpers/updateGroupSeenBorder";
import { showMediaLoadingShimmer, hideMediaLoadingShimmer } from "@/helpers/mediaLoadingShimmer";
import { isMediaImageReady } from "@/helpers/mediaUtils";
import { queryHeaderElements, queryHeaderElementsInElement } from "@/helpers/headerShadow";
import { queryCtaElementsInElement } from "@/helpers/ctaShadow";
import { isReelLike } from "@/helpers/isReelLike";

// 動画を開いた時に一番初めに実行される関数
export const playEventHandler = () => {
  const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
  if (!activeSlide) return;
  const { isMobile, frame } = window.storeeelItems;
  const isMobileCircle = isMobile && frame === 'circle';
  const video = isMobileCircle
    ? activeSlide.querySelector('.storeeel-movie.active')
    : activeSlide.querySelector('.storeeel-movie');
  if (video) {
    if (video._onLoadedMetadataForProgress) {
      video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
      video._onLoadedMetadataForProgress = null;
    }
    if (video._onPlayingForProgress) {
      video.removeEventListener('playing', video._onPlayingForProgress);
      video._onPlayingForProgress = null;
    }

    const wrapper = activeSlide.querySelector('.slide-media-wrapper');
    // square/tile フレームなら常に先頭、それ以外は window.moviePosition または 0
    const index = (!isMobile && isReelLike(frame))
      ? 0
      : (window.moviePosition || 0);
    const banners = isMobileCircle
      ? queryHeaderElementsInElement('.storeeel-movie-select_banner', activeSlide)
      : queryHeaderElements('.storeeel-movie-select_banner');
    const banner = banners[index];
    const hasProgress = Boolean(banner && (banner.classList.contains('active') || banner.classList.contains('completed')));

    if (hasProgress) {
      hideMediaLoadingShimmer(wrapper);
    } else {
      showMediaLoadingShimmer(wrapper);
      if (isMobileCircle) {
        prepareCurrentProgressBarInElement(index, activeSlide);
      } else {
        prepareCurrentProgressBar(index);
      }
    }

    const isStillActive = () => {
      const slide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      return !!slide && slide === activeSlide && slide.contains(video);
    };

    const cleanup = () => {
      if (video._onLoadedMetadataForProgress) {
        video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
        video._onLoadedMetadataForProgress = null;
      }
      if (video._onPlayingForProgress) {
        video.removeEventListener('playing', video._onPlayingForProgress);
        video._onPlayingForProgress = null;
      }
    };

    const startProgress = (duration) => {
      if (isMobileCircle) {
        animateCurrentProgressBarInElement(duration, index, activeSlide);
      } else {
        animateCurrentProgressBar(duration, index);
      }
      hideMediaLoadingShimmer(wrapper);
    };

    const tryStartProgress = () => {
      if (!isStillActive()) {
        cleanup();
        hideMediaLoadingShimmer(wrapper);
        return false;
      }
      if (banner && (banner.classList.contains('active') || banner.classList.contains('completed'))) {
        hideMediaLoadingShimmer(wrapper);
        cleanup();
        return false;
      }
      if (video.paused || video.ended) return false;
      if (video.readyState < 2) return false;
      const duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) return false;
      startProgress(duration);
      cleanup();
      return true;
    };

    if (!hasProgress) {
      // 再生開始まで進捗を開始しない
      video._onLoadedMetadataForProgress = () => { tryStartProgress(); };
      video._onPlayingForProgress = () => { tryStartProgress(); };
      video.addEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
      video.addEventListener('playing', video._onPlayingForProgress);
      tryStartProgress();
    }
  }

  const image = isMobileCircle
    ? activeSlide.querySelector('.storeeel-media-image.active')
    : activeSlide.querySelector('.storeeel-media-image');
  if (image && !window.storeeelItems.isMobile) {
    const wrapper = activeSlide.querySelector('.slide-media-wrapper');
    const progressIndex = window.moviePosition || 0;
    const banners = queryHeaderElements('.storeeel-movie-select_banner');
    const banner = banners[progressIndex];
    const hasProgress = Boolean(banner && (banner.classList.contains('active') || banner.classList.contains('completed')));

    if (hasProgress) {
      hideMediaLoadingShimmer(wrapper);
    } else {
      showMediaLoadingShimmer(wrapper);
      prepareCurrentProgressBar(progressIndex);
    }

    if (image._onLoadForProgress) {
      image.removeEventListener('load', image._onLoadForProgress);
      image._onLoadForProgress = null;
    }
    if (image._onErrorForProgress) {
      image.removeEventListener('error', image._onErrorForProgress);
      image._onErrorForProgress = null;
    }

    const cleanup = () => {
      if (image._onLoadForProgress) {
        image.removeEventListener('load', image._onLoadForProgress);
        image._onLoadForProgress = null;
      }
      if (image._onErrorForProgress) {
        image.removeEventListener('error', image._onErrorForProgress);
        image._onErrorForProgress = null;
      }
    };

    const isStillActive = () => {
      const slide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      return !!slide && slide === activeSlide && slide.contains(image);
    };

    const startProgress = () => {
      if (!isStillActive()) {
        cleanup();
        hideMediaLoadingShimmer(wrapper);
        return;
      }
      animateCurrentProgressBar(5, progressIndex);
      hideMediaLoadingShimmer(wrapper);
      cleanup();
    };

    const tryStartProgress = () => {
      if (banner && (banner.classList.contains('active') || banner.classList.contains('completed'))) {
        hideMediaLoadingShimmer(wrapper);
        cleanup();
        return false;
      }
      if (!isMediaImageReady(image)) return false;
      startProgress();
      return true;
    };

    if (!hasProgress) {
      image._onLoadForProgress = () => { tryStartProgress(); };
      image._onErrorForProgress = () => {
        hideMediaLoadingShimmer(wrapper);
        cleanup();
      };
      image.addEventListener('load', image._onLoadForProgress);
      image.addEventListener('error', image._onErrorForProgress);
      tryStartProgress();
    }
  }

  if (window.storeeelItems.isMobile && window.storeeelItems.frame === 'circle') {
    // activeSlide以外のCTAボタンを非表示にする
    const allWrappers = document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide .slide-media-wrapper');
    allWrappers.forEach((wrapper) => {
      // 現在のグループID以外のCTAボタンを非表示にする
      const groupId = wrapper.getAttribute('group-id');
      if (groupId !== window.iconId) {
        const ctaButtons = queryCtaElementsInElement('.ctaFrame', wrapper);
        ctaButtons.forEach(ctaButton => {
          ctaButton.style.visibility = 'hidden';
        });
      }
    });
  }

  // SP + square/tile（リール型）の view イベントは groupSwipeAction 側で制御しているため、
  // ここで送ると初回クリック時に二重送信になる。
  if (window.storeeelItems?.isMobile && isReelLike(window.storeeelItems?.frame)) {
    return;
  }

  if (window.__storeeelSkipInitialPlayMediaViewTracking) {
    // 共有リンク初期表示時は別経路で送信済みのため、ここでの初回送信は抑止する。
    delete window.__storeeelSkipInitialPlayMediaViewTracking;
    return;
  }

  // GA4 viewイベントを送信
  let mediaName;
  let mediaId;
  if (video) {
    mediaName = video.getAttribute('media-name');
    mediaId = video.getAttribute('media-id');
  } else if (image) {
    mediaName = image.getAttribute('media-name');
    mediaId = image.getAttribute('media-id');
  }
  const mediaWrapper = activeSlide.querySelector('.slide-media-wrapper');
  const mediaAssetType = mediaWrapper?.dataset?.assetType;
  const isFreqInsert = mediaWrapper?.dataset?.isFrequencyInsertContent === 'true';
  const freqContentId = mediaWrapper?.dataset?.frequencyInsertContentId || '';
  const groupId = isReelLike(window.storeeelItems.frame) ? null : window.iconId;
  trackMediaView(mediaName, mediaId, groupId, mediaAssetType, isFreqInsert, freqContentId);

  // localStorageに保存
  setLocalStorage(mediaId);
};

function trackMediaView(mediaName, mediaId, groupId, mediaAssetType, isFreqInsert = false, freqContentId = '') {
  if (isFreqInsert) {
    trackStoreeelEvent('storeeel_frequency_insert_media_view', {
      storeeel_name: mediaName,
      ...buildMediaAssetIdParam(freqContentId || mediaId, mediaAssetType),
      campaign_id: window.storeeelItems.id,
      ...(groupId ? { group_id: groupId } : {}),
    });
  } else {
    trackStoreeelEvent('storeeel_media_asset_view', buildMediaAssetViewParams({
      storeeelName: mediaName,
      groupId,
      mediaId,
      campaignId: window.storeeelItems.id,
      mediaAssetType,
    }));
  }
}

function setLocalStorage(mediaId) {
  const isSquare = isReelLike(window.storeeelItems?.frame);
  const isMobileCircle = window.storeeelItems?.isMobile && window.storeeelItems?.frame === 'circle';
  const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
  const mediaAssetType = activeSlide?.querySelector('.slide-media-wrapper')?.dataset?.assetType;
  const video = isMobileCircle
    ? activeSlide?.querySelector('.storeeel-movie.active')
    : activeSlide?.querySelector('.storeeel-movie');
  const image = isMobileCircle
    ? activeSlide?.querySelector('.storeeel-media-image.active')
    : activeSlide?.querySelector('.storeeel-media-image');
  const groupId = isSquare
    ? null
    : (video?.getAttribute('group-id') || image?.getAttribute('group-id') || window.iconId || null);

  localStorageRepositories.get("mediaView").set(
    mediaId,
    window.storeeelItems.id,
    groupId,
    mediaAssetType
  )
  // 無期限の既読フラグも保存（スライド型は保存しない）
  try {
    if (window.storeeelItems?.frame !== 'square') {
      const contentId = video?.getAttribute('content-id') || image?.getAttribute('content-id');
      if (contentId) localStorageRepositories.get("contentSeen").set(contentId);
      if (groupId) updateGroupSeenBorder(groupId);
    }
  } catch { void 0; }
}
